'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { INavigationGroup, INavigationItem, NavigationLevel } from '@workspace/interfaces/header';
import { resetToRootLevel, isNavigationGroup } from '@workspace/lib/header.utils';
import { useHeader as useHeaderData } from './useHeader';

export const useHeaderModel = () => {
  const { headerData, loading, error } = useHeaderData();
  const pathname = usePathname();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navigationStack, setNavigationStack] = useState<NavigationLevel[]>([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [activePath, setActivePath] = useState(pathname);
  const mobileNavRef = useRef<HTMLDivElement | null>(null);

  // Memoize the root navigation stack to prevent unnecessary recalculations
  const rootNavigationStack = useMemo(() => {
    return headerData?.navigationItems ? resetToRootLevel(headerData.navigationItems) : [];
  }, [headerData?.navigationItems]);

  useEffect(() => {
    setActivePath(pathname);
  }, [pathname]);

  useEffect(() => {
    setNavigationStack(rootNavigationStack);
  }, [rootNavigationStack]);

  // Memoize the scroll prevention function to avoid recreating it on every render
  const preventScroll = useCallback((event: Event) => {
    const menu = mobileNavRef.current;
    const targetNode = event.target as Node | null;
    if (!menu || !targetNode || !menu.contains(targetNode)) event.preventDefault();
  }, []);

  // Memoize the cleanup function for scroll effects
  const cleanupScrollEffects = useCallback(
    (restoreY: number) => {
      return () => {
        const propertiesToRemove = ['overflow', 'position', 'top', 'left', 'right', 'width'];
        propertiesToRemove.forEach((property) => document.body.style.removeProperty(property));
        window.scrollTo(0, restoreY);
        document.removeEventListener('wheel', preventScroll as EventListener);
        document.removeEventListener('touchmove', preventScroll as EventListener);
      };
    },
    [preventScroll],
  );

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const restoreY = window.scrollY || window.pageYOffset || 0;

    // Apply scroll lock styles
    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${restoreY}px`,
      left: '0',
      right: '0',
      width: '100%',
      overflow: 'hidden',
    });

    // Add event listeners with memoized handler
    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return cleanupScrollEffects(restoreY);
  }, [isMobileMenuOpen, preventScroll, cleanupScrollEffects]);

  // Memoize the reset navigation function to avoid recreating it
  const resetNavigation = useCallback(() => {
    setCurrentLevel(0);
    setNavigationStack(rootNavigationStack);
  }, [rootNavigationStack]);

  // Optimize mobile menu functions with useCallback
  const openMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(true);
    resetNavigation();
  }, [resetNavigation]);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    resetNavigation();
  }, [resetNavigation]);

  const goBack = useCallback(() => {
    if (currentLevel > 0) {
      setCurrentLevel((level) => level - 1);
      setNavigationStack((prev) => prev.slice(0, -1));
    }
  }, [currentLevel]);

  // Memoize the current navigation level data
  const currentNavigationLevel = useMemo(() => {
    return navigationStack[currentLevel];
  }, [navigationStack, currentLevel]);

  const handleItemClick = useCallback(
    (item: INavigationItem | INavigationGroup) => {
      if (isNavigationGroup(item)) {
        if (item.items && item.items.length > 0) {
          const newLevel = currentLevel + 1;
          const newLevelData = {
            title: item.groupTitle,
            items: item.items,
            parentTitle: currentNavigationLevel?.title,
          };

          setNavigationStack((prev) => [...prev, newLevelData]);
          setCurrentLevel(newLevel);
          return;
        }
      }
    },
    [currentLevel, currentNavigationLevel],
  );

  // Memoize the return object to prevent unnecessary re-renders in consuming components
  return useMemo(
    () => ({
      headerData,
      loading,
      error,
      isMobileMenuOpen,
      navigationStack,
      currentLevel,
      activePath,
      mobileNavRef,
      openMobileMenu,
      closeMobileMenu,
      goBack,
      handleItemClick,
      setActivePath,
    }),
    [
      headerData,
      loading,
      error,
      isMobileMenuOpen,
      navigationStack,
      currentLevel,
      activePath,
      openMobileMenu,
      closeMobileMenu,
      goBack,
      handleItemClick,
    ],
  );
};
